// The real browser behind the engine's Deps. The only file in src/audio that touches window,
// document, fetch, Audio or AudioContext.

import type { StorageLike } from './settings.ts';
import type { ContextLike, Deps, ElementLike } from './webaudio.ts';

// Events that count as a user gesture for autoplay. pointerdown is left out on purpose: for touch
// it does not activate the page, pointerup does.
const GESTURE_EVENTS = ['pointerup', 'keydown', 'touchend', 'click'] as const;

class Stream implements ElementLike {
  readonly audio: HTMLAudioElement;

  constructor(url: string) {
    this.audio = new Audio();
    this.audio.crossOrigin = 'anonymous';
    this.audio.preload = 'auto';
    this.audio.src = url;
  }

  get currentTime(): number {
    return this.audio.currentTime;
  }

  set currentTime(seconds: number) {
    this.audio.currentTime = seconds;
  }

  get duration(): number {
    return this.audio.duration;
  }

  set onerror(run: (() => void) | null) {
    this.audio.onerror = run;
  }

  get onerror(): (() => void) | null {
    return this.audio.onerror as (() => void) | null;
  }

  play(): Promise<void> {
    return this.audio.play();
  }

  // Dropping the source tells the browser to stop downloading; a paused element would keep buffering.
  dispose(): void {
    this.audio.pause();
    this.audio.onerror = null;
    this.audio.removeAttribute('src');
    this.audio.load();
  }
}

function wrap(ctx: AudioContext): ContextLike {
  return {
    get currentTime() {
      return ctx.currentTime;
    },
    destination: ctx.destination,
    get running() {
      return ctx.state === 'running';
    },
    resume: () => ctx.resume(),
    close: () => ctx.close(),
    createGain: () => ctx.createGain(),
    createBufferSource: () => ctx.createBufferSource() as unknown as ReturnType<ContextLike['createBufferSource']>,
    createMediaElementSource: (element) => ctx.createMediaElementSource((element as Stream).audio),
    decodeAudioData: (bytes) => ctx.decodeAudioData(bytes),
  };
}

function storage(): StorageLike | null {
  // Reading window.localStorage itself throws when site data is blocked.
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

async function get(url: string): Promise<Response> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`${url}: HTTP ${response.status}`);
  }
  return response;
}

export function browserDeps(baseUrl: string): Deps {
  return {
    baseUrl,
    createContext: () => {
      if (typeof AudioContext === 'undefined') {
        return null;
      }
      return wrap(new AudioContext({ latencyHint: 'playback' }));
    },
    createElement: (url) => new Stream(url),
    fetchBytes: async (url) => (await get(url)).arrayBuffer(),
    fetchJson: async (url) => (await get(url)).json(),
    storage: storage(),
    setTimer: (run, ms) => window.setTimeout(run, ms),
    clearTimer: (id) => window.clearTimeout(id),
    onGesture: (run) => {
      const options = { capture: true, passive: true };
      GESTURE_EVENTS.forEach((type) => document.addEventListener(type, run, options));
      return () => GESTURE_EVENTS.forEach((type) => document.removeEventListener(type, run, options));
    },
  };
}
